<template>
  <div class="chat-view">
    <div class="loader" v-if="loading">
      <div class="spinner"></div>
    </div>

    <!-- Сайдбар (скрыт на мобиле) -->
    <div :class="['sidebar', {visible: menuVisible}]">
      <div class="sidebar-scroll">
        <new-chat :onCreated="onChatCreated" />
        <chat-item
          v-for="thread in threads"
          :key="thread.id"
          :thread="thread"
          :current-thread="currentThread"
          :user-name="userName"
          @onEdited="onChatEdited"
          @onSelected="selectThread(thread)"
          @onDelete="deleteThread(thread.id)"
        />
      </div>
      <div class="support">
        <div class="admin-link" v-if="isAdmin">
          <a href="#" @click.prevent="$emit('goToAdmin')">Панель администратора</a>
        </div>
        <div class="logout-link" v-if="allowLogout">
          <a href="#" @click.prevent="logout">Выйти</a>
          <span class="userName">({{displayUserName}})</span>
        </div>
        <div class="support-link">
          <a href="https://support.fitness365.ru/documentation/%d0%bc%d0%be%d0%b1%d0%b8%d0%bb%d1%8c%d0%bd%d0%be%d0%b5-%d0%bf%d1%80%d0%b8%d0%bb%d0%be%d0%b6%d0%b5%d0%bd%d0%b8%d0%b5-%d1%82%d1%80%d0%b5%d0%bd%d0%b5%d1%80%d0%b0/" target="_blank">Руководство пользователя</a>
        </div>
        <div class="support-link">
          <a href="https://max.ru/id3329054887_bot" target="_blank">Обратиться в MAX</a>
        </div>
        <div class="support-link">
          <a href="https://t.me/fit365_bot" target="_blank">Обратиться в Telegram</a>
        </div>        
        <div class="support-link">
          <a href="#" @click.prevent="whatsApp">Обратиться в WhatsApp</a>
        </div>
        <div class="support-link">
          <a href="#" @click.prevent="jivoRun">Обратиться в чат</a>
        </div>
      </div>
    </div>

    <div class="chat-area" v-if="currentThread">
      <!-- Кнопка поделиться -->
      <button class="share-button" @click="shareThread" v-if="currentThread && currentThread.id !== -1 && messages && messages.length > 0">
        <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 
                  12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.02-4.11c.54.5 
                  1.25.81 2.07.81 1.66 0 3-1.34 
                  3-3s-1.34-3-3-3-3 1.34-3 
                  3c0 .24.04.47.09.7l-7.02 
                  4.11c-.54-.5-1.25-.81-2.07-.81-1.66 
                  0-3 1.34-3 3s1.34 3 
                  3 3c.82 0 1.53-.31 2.07-.81l7.12 
                  4.16c-.05.21-.09.43-.09.65 0 
                  1.61 1.31 2.92 2.92 
                  2.92s2.92-1.31 
                  2.92-2.92-1.31-2.92-2.92-2.92z"/>
        </svg>
      </button>


      <div ref="messagesContainer" class="messages" @scroll="updateScrollButtonVisibility">
        <template v-if="messages.length === 0">
          <div class="no-messages">
            <div class="no-messages-text">Тут пока пусто — задайте свой первый вопрос, и я помогу Вам разобраться!</div>
            <div class="new-chat-hint">Для новых вопросов лучше создавать новый чат, а не продолжать текущий. Так ответы будут более точными.</div>
          </div>
        </template>
        <template v-else>
          <message v-for="msg in messages" :message="msg" :key="msg.id" />
        </template>
      </div>

      <button class="scroll-to-bottom" v-if="showScrollButton" @click="scrollMessagesToBottom" title="Прокрутить вниз">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 16.5l-7-7 1.41-1.41L11 13.67V4h2v9.67l4.59-4.58L19 10.5z" />
          <path d="M5 19h14v2H5z" />
        </svg>
      </button>

      <div class="input-area">
        <button class="show-menu-button" @click="toggleMenu()">M</button>
        <input
          v-model="newMessage"
          @keyup.enter="sendMessage"
          :disabled="loading || !currentThread || atMessageLimit"
          placeholder="Введите вопрос и нажмите Enter..."
          autocomplete="off"
        />

        <button
          class="send-button"
          :class="{ 'send-button--inactive': loading || !newMessage.trim() || atMessageLimit }"
          @click="sendMessage"
          title="Отправить сообщение"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
        </button>
        
        <button
          class="mic-button"
          :class="{ 'mic-button--active': isRecording }"
          v-if="speechRecognitionSupported"
          :disabled="loading || !currentThread || atMessageLimit"
          @click="toggleSpeechRecognition"
          :title="isRecording ? 'Остановить запись' : 'Голосовой ввод'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z" />
            <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.08A7 7 0 0 0 19 11z" />
          </svg>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { yandexApi, postMessageStream } from './api/yandex-api';
import { getThreads, addThread, updateThread, removeThread, touchThread, pruneStaleThreads } from './api/thread-storage';
import message from './message.vue';
import newChat from './new-chat.vue';
import chatItem from './chat-item.vue';
import VueJivosite from '@bchteam/vue-jivosite';
import Vue from 'vue';

export default {
  props: {
    userName: {
      type: String,
    },
    displayUserName: {
      type: String,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      threads: [],
      currentThread: null,
      messages: [],
      newMessage: '',
      loading: false,
      menuVisible: false,
      showScrollButton: false,
      isRecording: false,
      speechRecognition: null,
      speechRecognitionSupported: false,
    };
  },
  components: {
    message,
    newChat,
    chatItem,
  },
  computed: {
    allowLogout() {
      return true;
      //return ALLOWED_USERNAMES.includes(storedName) || this.userName.indexOf("@") > -1;;
    },
    // GetConversationItemsAsync fetches at most 100 items — once a thread is this full, further
    // messages would just fall outside that window, so push the user towards a new chat instead.
    atMessageLimit() {
      return this.messages.length >= 99;
    },
  },
  methods: {
    toggleMenu() {
      this.menuVisible = !this.menuVisible;
    },
    async shareThread() {
      if (!this.currentThread || !this.currentThread.id) return;
      const url = `${window.location.origin}${window.location.pathname}?sharedThreadId=${this.currentThread.id}`;
      try {
        yandexApi.post(`/shareThread`, { id: this.currentThread.id });
        await navigator.clipboard.writeText(url);
        Vue.toasted.show(`Ссылка на чат скопирована в буфер обмена.`, {
          type: 'success',
          duration: 3000
        });
      } catch (err) {
        Vue.toasted.show("Не удалось скопировать ссылку", { type: "error", duration: 5000 });
        console.error("Ошибка копирования:", err);
      }
    },
    async fetchThreads() {
      this.loading = true;

      // Yandex's Conversations API has no "list conversations" endpoint — this app is the sole
      // owner of the thread list, kept entirely in localStorage (see thread-storage.js).
      const localThreads = getThreads(this.userName);

      this.threads = localThreads.map(t => ({
        ...t,
        processing: false
      }));

      // Quietly drop conversations with no activity for 3+ days: filter them out of what's
      // about to be shown, then delete locally + on the server in the background. Never
      // surfaced to the user beyond a console log.
      this.pruneStaleThreadsInBackground();

      if (this.threads[0]) {
        await this.selectThread(this.threads[0] || null);
      }
      else {
        console.log('Создание чата');
        const runBody = {
          userId: this.userName,
          name: 'Новый чат',
          description: "",
        };

        try {
          const { data: thread } = await yandexApi.post(`/thread`, runBody);
          thread.processing = false;
          this.threads.unshift(thread);
          addThread(this.userName, thread);
          await this.selectThread(thread)
        } catch (error) {
          console.error('Ошибка при создании чата:', error);
        }
      }

      this.loading = false;
    },
    async fetchMessages() {
      try {
        this.loading = true;
        const { data } = await yandexApi.get(`/messages`, { params: { threadId: this.currentThread.id } });
        // User turns come back from Yandex without an item id (only generated assistant items have one) -
        // fall back to a synthetic key so Vue's v-for doesn't get duplicate/undefined keys.
        this.messages = (data || []).map((m, i) => ({ ...m, id: m.id || `local-${i}` }));//.reverse();
        this.$nextTick(() => {
          this.scrollMessagesToBottom();
        });
        this.loading = false;

        if (this.atMessageLimit) {
          Vue.toasted.show('Достигнут лимит сообщений чата, пожалуйста начните новый чат', { type: 'error', duration: 8000 });
        }

        if(this.currentThread && this.currentThread.streamingMessage) {
          this.messages.push(this.currentThread.streamingMessage);
          this.$nextTick(() => {
            this.scrollMessagesToBottom();
          });
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        this.loading = false;
      }
    },
    async selectThread(thd) {
      this.currentThread = thd;
      await this.fetchMessages();
    },
    pruneStaleThreadsInBackground() {
      const stale = pruneStaleThreads(this.userName);
      if (stale.length === 0) return;

      console.log(`Удаление ${stale.length} неактивных бесед (нет сообщений более 3 дней):`, stale);

      const staleIds = new Set(stale.map(t => t.id));
      this.threads = this.threads.filter(t => !staleIds.has(t.id));

      stale.forEach(t => {
        yandexApi.post(`/deleteThread?threadId=${t.id}`)
          .then(() => console.log(`Беседа ${t.id} удалена на сервере`))
          .catch(err => console.error(`Не удалось удалить беседу ${t.id} на сервере:`, err));
      });
    },
    async deleteThread(id) {
      this.threads = this.threads.filter(thread => thread.id !== id);
      removeThread(this.userName, id);
      yandexApi.post(`/deleteThread?threadId=${id}`);
      if (this.currentThread === id) {
        this.currentThread = this.threads.length ? this.threads[0] : null;
        if (this.currentThread) await this.fetchMessages();
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      if (this.atMessageLimit) {
        Vue.toasted.show('Достигнут лимит сообщений чата, пожалуйста начните новый чат', { type: 'error', duration: 5000 });
        return;
      }

      touchThread(this.userName, this.currentThread.id);

      const userMessage = {
        id: Date.now().toString(),
        text: this.newMessage,
        created: Date.now().toString(),
        role: 0,
        status: 'COMPLETED',
      };

      this.messages.push(userMessage);
      if(this.messages.length == 1) {
        this.currentThread.name = this.newMessage;
        updateThread(this.userName, this.currentThread.id, { name: this.newMessage });
      }

      let assistantMessage = {
        id: 'streaming-' + Date.now(),
        text: '',
        created: Date.now().toString(),
        role: 1,
        status: 'chunk',
        citations: []
      };
      this.messages.push(assistantMessage);

      this.currentThread.processing = true;
      this.currentThread.streamingMessage = assistantMessage;

      // Scroll once to bring the just-sent message into view. While the response is streaming in,
      // we deliberately stop auto-scrolling (see the onEvent handler below) so a long reply doesn't
      // yank the view around — a "scroll to bottom" arrow appears instead once content overflows.
      this.$nextTick(() => {
        this.scrollMessagesToBottom();
      });

      const runBody = {
        threadId: this.currentThread.id,
        message: this.newMessage,
      };

      let workingThread = this.currentThread;

      try {
        await postMessageStream(runBody, (msg) => {
          if (msg.status === 'chunk') {
            assistantMessage.text = msg.text;
          } else if (msg.status === 'done') {
            assistantMessage.id = msg.id;
            assistantMessage.text = msg.text;
            assistantMessage.citations = msg.citations;
            assistantMessage.status = 'done';
          } else if (msg.status === 'error') {
            console.error('Ошибка потока:', msg.text);
            assistantMessage.text = 'Упс, что-то пошло не так. Попробуйте позже.';
            assistantMessage.status = 'ERROR';
          }
          this.$nextTick(() => {
            this.updateScrollButtonVisibility();
          });
        }, (error) => {
          console.error('Ошибка потока:', error); 
          workingThread.processing = false;
          workingThread.streamingMessage = null;
        }, () => {
          console.log('done');
          workingThread.processing = false;
          workingThread.streamingMessage = null;
        });
      } catch (e) {
        workingThread.processing = false;
        workingThread.streamingMessage = null;
        console.error('Ошибка потока:', e);
        assistantMessage.text = 'Ошибка при получении ответа';
        assistantMessage.status = 'ERROR';
      } finally {
        this.newMessage = '';
      }
    },
    async onChatCreated(thread) {
      this.threads.unshift(thread);
      addThread(this.userName, thread);
      await this.selectThread(thread);
    },
    onChatEdited(thread) {
      updateThread(this.userName, thread.id, { name: thread.name });
    },
    scrollMessagesToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      this.showScrollButton = false;
    },
    updateScrollButtonVisibility() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      this.showScrollButton = distanceFromBottom > 40;
    },
    logout() {
      localStorage.removeItem('USER_NAME');
      window.location.reload();
    },
    whatsApp() {
      const phoneNumber = '74999990185';
      //const message = 'Здравствуйте! Я хочу задать вопрос.';
      //const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      const url = `https://wa.me/${phoneNumber}`;
      window.open(url, '_blank');
    },
    jivoRun() {
      if (window.jivo_api)
        window.jivo_api.open();
    },
    initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) return;

      this.speechRecognitionSupported = true;
      this.speechRecognition = new SpeechRecognition();
      this.speechRecognition.lang = 'ru-RU';
      this.speechRecognition.continuous = false;
      this.speechRecognition.interimResults = false;

      this.speechRecognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join(' ');
        this.newMessage = (this.newMessage ? this.newMessage + ' ' : '') + transcript.trim();
      };

      this.speechRecognition.onerror = (event) => {
        console.error('Ошибка распознавания речи:', event.error);
        if (event.error !== 'no-speech') {
          Vue.toasted.show('Не удалось распознать речь', { type: 'error', duration: 3000 });
        }
      };

      this.speechRecognition.onend = () => {
        this.isRecording = false;
      };
    },
    toggleSpeechRecognition() {
      if (!this.speechRecognition) return;

      if (this.isRecording) {
        this.speechRecognition.stop();
        this.isRecording = false;
      } else {
        this.speechRecognition.start();
        this.isRecording = true;
      }
    },
    initJivosite() {
				window.jivo_onLoadCallback = () => {
					window.jivo_cstm_widget = $('<div>').attr('id', 'jivo_custom_widget').appendTo('body')[0];
				};
				window.jivo_onOpen = () => {
					$('#textback_widget').class('');
				};
				Vue.use(VueJivosite, {widgetId: 'kjvg9XrGkN'});
		},
  },
  mounted() {
    this.currentThread = { id: -1 };
    this.fetchThreads();
    this.initJivosite();
    this.initSpeechRecognition();
  },
  beforeDestroy() {
    if (this.speechRecognition) {
      this.speechRecognition.stop();
    }
  },
};
</script>

<style scoped>

.share-button {
  position: absolute;
  top: 10px;
  right: 30px;
  background: #fff;        /* белый фон */
  border: none;
  border-radius: 50%;      /* круглая форма */
  /*box-shadow: 0 1px 4px rgba(0,0,0,0.15); /* лёгкая тень */
  color: #f15540;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  z-index: 10;
  opacity: 0.8;
}

.share-button:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}

.loader {
  position: absolute;
  bottom: 70px;
  right: 30px;
  z-index: 10;
}

.spinner {
  width: 25px;
  height: 25px;
  border: 3.5px solid rgba(250, 241, 241, 0.4);
  border-left-color: #e74c3c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.chat-view {
  display: flex;
  height: 100%;
  overflow: hidden;
}

/* Сайдбар и new-chat */

.sidebar {
  width: 250px;
  background: #f0f0f0;
  padding: 2px 0px 10px 10px;
  margin-right: 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-scroll {
  overflow-y: auto;
  flex: 1;
  padding: 2px 10px 10px 0px;
  margin-bottom: 10px;
}

/* Основная чат-зона */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative; /* Добавлено для корректного absolute позиционирования input-area */
  min-height: 0;
}

.input-area {
  display: flex;
  align-items: center; /* выравнивание по центру по вертикали */
  padding: 10px 7px 10px 10px;
  border-top: 1px solid #ccc;
  background: #fff;
  position: sticky;
  gap: 10px; /* расстояние между input и кнопкой */
}

.input-area input {
  flex: 1;
  padding: 0.5rem;
  box-sizing: border-box;
}

.send-button, .show-menu-button, .mic-button {
  background: none;
  border: none;
  color: #f15540;
  cursor: pointer;
  padding: 6px 6px 6px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-button:disabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: default;
}

.mic-button--active {
  color: #e74c3c;
  animation: mic-pulse 1s ease-in-out infinite;
}

@keyframes mic-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.show-menu-button {
  display: none;
}

.send-button--inactive {
  opacity: 0.4;
  pointer-events: none;
  cursor: default;
}

/* input:disabled {
  background-color: #f5f5f5;
  border: #3e8ef7 1px solid;
  color: #999;
  cursor: not-allowed;
} */

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.scroll-to-bottom {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 50%;
  color: #f15540;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 5;
}

.scroll-to-bottom:hover {
  background: #f7f7f7;
}

.no-messages {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 420px;
  padding: 0 20px;
  color: #888;
  font-size: 14px;
  user-select: none;
  text-align: center;
  box-sizing: border-box;
}

.no-messages-text {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #444;
  margin-bottom: 14px;
}

.new-chat-hint {
  font-size: 13px;
  color: #aaa;
}

.support {
  padding: 10px 10px 20px 10px;
  border-top: 1px solid #ccc;
  font-size: 14px;
  color: #555;
  background: #f0f0f0;
  position: sticky;
  bottom: 0;
  z-index: 1;
}

.admin-link,
.logout-link {
  padding-top: 5px;
  text-align: start;
  cursor: pointer;
  color: #006eff;
  font-size: 12px;
}

.support-link {
  cursor: pointer;
  color: #006eff;
  padding-top: 10px;
  font-size: 12px;
  max-width: 250px;
  text-align: start;
}

/* === Адаптивные стили для мобильных и планшетов === */

@media (max-width: 768px) {
  .share-button:not(.visible) {
    display: none;
  }
  
  .sidebar:not(.visible) {
    display: none;
  }

  .show-menu-button {
    display: unset;
  }

  .chat-area {
    min-width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/chat-view.vue